(() => {
  if (!('customElements' in window) || customElements.get('dd-recipes-infinite-loader')) return;

  class DDRecipesInfiniteLoader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.grid = this.querySelector('.dd-recipes-index__grid');
      this.loader = this.querySelector('[data-dd-recipes-loader]');
      this.loadingStatus = this.querySelector('[data-dd-recipes-loading]');
      this.retryButton = this.querySelector('[data-dd-recipes-retry]');
      this.pagination = this.querySelector('[data-dd-recipes-pagination]');
      if (!this.grid || !this.loader || !this.pagination || !('IntersectionObserver' in window)) return;

      this.initialized = true;
      this.loading = false;
      this.controller = null;
      this.recipeKeys = new Set([...this.grid.querySelectorAll('[data-dd-recipe-key]')].map((item) => this.keyForItem(item)).filter(Boolean));
      this.retryButton?.addEventListener('click', () => this.retry());
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      }, { rootMargin: '600px 0px' });

      this.classList.add('is-enhanced');
      this.loader.hidden = false;
      if (this.nextPageLink()) this.observer.observe(this.loader);
      else this.finish();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    keyForItem(item) {
      return item.dataset.ddRecipeKey || item.querySelector('a[href]')?.href || '';
    }

    nextPageLink() {
      return this.pagination.querySelector('a.pagination__item--prev[href]');
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      if (isLoading) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      if (this.loadingStatus) this.loadingStatus.hidden = !isLoading;
      if (this.retryButton && isLoading) this.retryButton.hidden = true;
    }

    async loadNextPage() {
      const nextLink = this.nextPageLink();
      if (this.loading || !nextLink) {
        if (!nextLink) this.finish();
        return;
      }

      this.observer?.unobserve(this.loader);
      this.setLoading(true);
      this.classList.remove('has-error');
      this.controller = new AbortController();

      try {
        const nextPageUrl = new URL(nextLink.href);
        const sectionId = this.closest('[id^="shopify-section-"]')?.id.slice('shopify-section-'.length);
        if (sectionId) nextPageUrl.searchParams.set('section_id', sectionId);

        const response = await fetch(nextPageUrl.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Recipe page request failed: ${response.status}`);

        const fetchedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextFeed = [...fetchedDocument.querySelectorAll('[data-dd-recipes-feed]')]
          .find((feed) => feed.dataset.ddRecipesScope === this.dataset.ddRecipesScope);
        if (!nextFeed) throw new Error('Recipe page scope changed');

        const currentPage = Number(this.dataset.currentPage);
        const nextPage = Number(nextFeed.dataset.currentPage);
        const nextPageCount = Number(nextFeed.dataset.pageCount);
        if (!Number.isInteger(nextPage) || !Number.isInteger(nextPageCount) || nextPage !== currentPage + 1 || nextPage > nextPageCount) {
          throw new Error('Recipe page sequence changed');
        }

        const nextGrid = nextFeed.querySelector('.dd-recipes-index__grid');
        const nextItems = nextGrid ? [...nextGrid.querySelectorAll(':scope > [data-dd-recipe-key]')] : [];
        const uniqueItems = nextItems.filter((item) => {
          const key = this.keyForItem(item);
          if (!key || this.recipeKeys.has(key)) return false;
          this.recipeKeys.add(key);
          return true;
        });
        if (!uniqueItems.length) throw new Error('Recipe page contained no new recipes');

        const fragment = document.createDocumentFragment();
        uniqueItems.forEach((item) => fragment.append(document.importNode(item, true)));
        this.grid.append(fragment);

        const nextPagination = nextFeed.querySelector('[data-dd-recipes-pagination]');
        this.pagination.replaceChildren(...(nextPagination ? [...nextPagination.childNodes].map((node) => document.importNode(node, true)) : []));
        this.dataset.currentPage = String(nextPage);
        this.dataset.pageCount = String(nextPageCount);
        this.setLoading(false);
        this.dispatchEvent(new CustomEvent('dd:recipes:appended', {
          bubbles: true,
          detail: { count: uniqueItems.length, page: nextPage },
        }));

        if (this.nextPageLink()) this.observer?.observe(this.loader);
        else this.finish();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showError();
        this.dispatchEvent(new CustomEvent('dd:recipes:error', { bubbles: true }));
      } finally {
        this.controller = null;
        if (this.loading) this.setLoading(false);
      }
    }

    showError() {
      this.observer?.unobserve(this.loader);
      this.classList.add('has-error');
      this.setLoading(false);
      if (this.retryButton) this.retryButton.hidden = false;
    }

    retry() {
      if (this.loading) return;
      this.classList.remove('has-error');
      if (this.retryButton) this.retryButton.hidden = true;
      this.loadNextPage();
    }

    finish() {
      this.observer?.disconnect();
      this.classList.add('is-complete');
      this.setLoading(false);
      this.loader.hidden = true;
    }
  }

  customElements.define('dd-recipes-infinite-loader', DDRecipesInfiniteLoader);
})();
